import { create } from 'zustand';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

export const useSupabaseStore = create((set, get) => ({
  supabase,
  rank: 0,
  isLoading: false,
  error: null,
  hasCheckedInToday: false,

  setPoints: (points) => set({ points }),
  setRank: (rank) => set({ rank }),
  
  checkDailyStatus: async (walletAddress) => {
    if (!walletAddress) return false;

    try {
      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('daily_check_ins')
        .select('id')
        .eq('wallet_address', walletAddress)
        .eq('check_date', today)
        .maybeSingle();

      if (error) throw error;
      
      set({ hasCheckedInToday: !!data });
      return !!data;
    } catch (error) {
      console.error('Error checking daily status:', error);
      return false;
    }
  },

  recordDailyCheckIn: async (walletAddress) => {
    if (!walletAddress) return false;
    
    try {
      // First check if already checked in
      const hasChecked = await get().checkDailyStatus(walletAddress);
      if (hasChecked) return false;

      const today = new Date().toISOString().split('T')[0];

      // Record the check-in
      const { error: checkInError } = await supabase
        .from('daily_check_ins')
        .insert([
          { 
            wallet_address: walletAddress,
            check_date: today
          }
        ]);

      if (checkInError) throw checkInError;

      set({ hasCheckedInToday: true });
      return true;
    } catch (error) {
      console.error('Error recording daily check-in:', error);
      return false;
    }
  },

  fetchPoints: async (walletAddress) => {
    if (!walletAddress) return;

    set({ isLoading: true, error: null });
    try {
      // Get current user's points
      const { data: userData, error: pointsError } = await supabase
        .from('members')
        .select('points')
        .eq('wallet_address', walletAddress)
        .single();

      if (pointsError) throw pointsError;
      const currentPoints = userData?.points || 0;

      // Get all users ordered by points to calculate rank
      const { data: rankings, error: rankError } = await supabase
        .from('members')
        .select('points')
        .order('points', { ascending: false });

      if (rankError) throw rankError;

      // Calculate rank (index + 1 of current user's points in sorted array)
      const rank = rankings.findIndex(user => user.points === currentPoints) + 1;

      set({ 
        points: currentPoints,
        rank: rank || rankings.length + 1 // If not found, place last
      });

      // Check daily check-in status
      await get().checkDailyStatus(walletAddress);
    } catch (error) {
      console.error('Error fetching points and rank:', error);
      set({ error: error.message });
    } finally {
      set({ isLoading: false });
    }
  },
}));